import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'antd';

const EMPTY_WEBVTT_TRACK = 'data:text/vtt;charset=utf-8,WEBVTT%0A%0A';

const getDisplayValue = (value, fallback = '') => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue || fallback;
  }

  return value ? String(value) : fallback;
};

const GalleryPreviewModal = ({
  open,
  onClose,
  resource,
  resourceType,
  onUnshare,
  isRemoving,
  unshareLabel,
  photoFallbackSrc,
}) => {
  const [isVideoReady, setIsVideoReady] = useState(false);

  const title = getDisplayValue(resource?.title);
  const description = getDisplayValue(resource?.description);
  const mediaUrl =
    resourceType === 'videos'
      ? resource?.playbackUrl || resource?.url || resource?.downloadurl
      : resource?.resolvedPhotoUrl ||
        resource?.url ||
        resource?.downloadurl ||
        resource?.playbackUrl;

  return (
    <Modal
      className="gallery-resource-preview-modal"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      closable={!isRemoving}
      maskClosable={!isRemoving}
      width={960}
    >
      {resource && mediaUrl ? (
        <div className="gallery-resource-preview-content">
          <div className="gallery-resource-preview-title" title={title}>
            {title}
          </div>

          <div
            className={`gallery-resource-preview-surface${
              resourceType === 'videos' ? ' gallery-resource-preview-surface-video' : ''
            }`}
          >
            {resourceType === 'videos' ? (
              <>
                {!isVideoReady ? <div className="gallery-resource-preview-video-loader" /> : null}
                <video
                  className={`gallery-resource-preview-media gallery-resource-preview-video${
                    isVideoReady ? '' : ' gallery-resource-preview-video-hidden'
                  }`}
                  src={mediaUrl}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  onLoadStart={() => setIsVideoReady(false)}
                  onLoadedData={() => setIsVideoReady(true)}
                  onCanPlay={() => setIsVideoReady(true)}
                  onError={() => setIsVideoReady(true)}
                >
                  <track kind="captions" srcLang="en" label="English" src={EMPTY_WEBVTT_TRACK} />
                </video>
              </>
            ) : (
              <img
                src={mediaUrl}
                alt={title}
                className="gallery-resource-preview-media gallery-resource-preview-image"
                onError={(event) => {
                  if (!photoFallbackSrc || event.currentTarget.dataset.fallbackApplied === 'true') {
                    return;
                  }

                  event.currentTarget.dataset.fallbackApplied = 'true';
                  event.currentTarget.src = photoFallbackSrc;
                }}
              />
            )}
          </div>

          <div className="gallery-resource-preview-description" title={description}>
            {description}
          </div>

          <div className="gallery-resource-preview-actions">
            <Button
              className="gallery-resource-preview-remove-button"
              onClick={() => onUnshare(resource)}
              loading={isRemoving}
              disabled={isRemoving}
            >
              <span className="gallery-resource-preview-remove-button-icon" aria-hidden="true">
                x
              </span>
              <span className="gallery-resource-preview-remove-button-label">{unshareLabel}</span>
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

GalleryPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  resource: PropTypes.object,
  resourceType: PropTypes.oneOf(['photos', 'videos']).isRequired,
  onUnshare: PropTypes.func.isRequired,
  isRemoving: PropTypes.bool,
  unshareLabel: PropTypes.string,
  photoFallbackSrc: PropTypes.string,
};

GalleryPreviewModal.defaultProps = {
  resource: null,
  isRemoving: false,
  unshareLabel: 'Unshare',
  photoFallbackSrc: '',
};

export default GalleryPreviewModal;
